import { useTranslation } from 'react-i18next';
import { AlertTriangle, X } from 'lucide-react';

interface SessionExpiredModalProps {
  isOpen: boolean;
  message?: string;
  onClose: () => void;
  onConfirm: () => void;
}

export function SessionExpiredModal({
  isOpen,
  message,
  onClose,
  onConfirm,
}: SessionExpiredModalProps) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  const normalizedMessage = (() => {
    const fallback =
      t(
        'session.expired.message',
        'Votre session a expiré. Veuillez vous reconnecter pour continuer.'
      );
    if (!message) {
      return fallback;
    }
    const trimmed = message.trim();
    if (!trimmed) {
      return fallback;
    }
    if (/invalid or expired token/i.test(trimmed)) {
      return fallback;
    }
    if (/forbidden|access forbidden|accès interdit/i.test(trimmed)) {
      return t(
        'session.expired.forbidden',
        'Access forbidden. Please sign in again.'
      );
    }
    return trimmed;
  })();

  return (
    <div className="fixed inset-0 z-[9999] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
          onClick={handleConfirm}
        />

        {/* Modal */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-3">
                <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900">
                  {t('session.expired.title', 'Session expirée')}
                </h3>
              </div>
              <button
                onClick={handleConfirm}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Message */}
            <div className="mb-6">
              <p className="text-sm text-gray-600">
                {normalizedMessage}
              </p>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-3">
              <button
                onClick={handleConfirm}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-colors"
              >
                {t('session.expired.confirm', 'Se reconnecter')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

